import React, { useMemo, useState, useEffect } from 'react';
import { Ship } from '../../interfaces/types';
import { ImageCache } from '../../services/imageCache';
import { TYPE_TRANSLATIONS } from '../../utils/translations';
import './ShipCatalogView.css';

interface ShipCatalogViewProps {
  ships: Ship[];
  fleetCounts: { user: Record<string, number>; others: Record<string, number> }; // Conteos separados por usuario actual y otros
  sortBy: 'manufacturer' | 'type' | 'name';
  onShipClick?: (ship: Ship) => void;
}

interface CatalogItemProps {
  ship: Ship;
  userCount: number; // Naves del usuario actual
  otherCount: number; // Naves de otros usuarios
  onClick: () => void;
}

const CatalogItem: React.FC<CatalogItemProps> = ({ ship, userCount, otherCount, onClick }) => {
  const [shipImage, setShipImage] = useState<string>('/media/naves/not_found.png');
  const [imageError, setImageError] = useState(false);

  // Función helper para convertir el nombre de la nave a formato URL
  const getCloudFrontImageUrl = (shipName: string): string => {
    // Convertir el nombre a minúsculas y reemplazar espacios con guiones si es necesario
    const normalizedName = shipName.toLowerCase().replace(/\s+/g, '-');
    return `https://d2gu8hui36xbjs.cloudfront.net/miniature-ships-isometric/${normalizedName}.webp`;
  };

  useEffect(() => {
    const loadImage = async () => {
      const cloudFrontUrl = getCloudFrontImageUrl(ship.name);
      try {
        // Intentar cargar la imagen de CloudFront
        const cachedImage = await ImageCache.getImage(cloudFrontUrl);
        setShipImage(cachedImage);
        setImageError(false);
      } catch (error) {
        // Si falla, usar imagen de fallback
        setImageError(true);
      }
    };
    loadImage();
  }, [ship.name]);

  return (
    <div
      className="catalog-item"
      onClick={onClick}
      title={ship.name}
    >
      <div className="catalog-item-image">
        <img
          src={imageError ? '/media/naves/not_found.png' : shipImage}
          alt={ship.name}
          onError={() => setImageError(true)}
        />
        {(userCount > 0 || otherCount > 0) && (
          <div className={`catalog-item-count ${userCount > 0 ? 'user-owned' : 'other-owned'}`}>
            {userCount > 0 ? userCount : otherCount}
          </div>
        )}
      </div>
      <div className="catalog-item-name">{ship.name}</div>
    </div>
  );
};

export const ShipCatalogView: React.FC<ShipCatalogViewProps> = ({
  ships,
  fleetCounts,
  sortBy,
  onShipClick
}) => {
  // Agrupar y ordenar naves según el criterio seleccionado (eliminando duplicados por nombre)
  const groupedShips = useMemo(() => {
    // Eliminar duplicados: solo mantener la primera ocurrencia de cada nombre de nave
    const uniqueShipsMap = new Map<string, Ship>();
    ships.forEach(ship => {
      if (!uniqueShipsMap.has(ship.name)) {
        uniqueShipsMap.set(ship.name, ship);
      }
    });
    const uniqueShips = Array.from(uniqueShipsMap.values());

    const groups: Record<string, Ship[]> = {};

    uniqueShips.forEach(ship => {
      let groupKey: string;
      
      switch (sortBy) {
        case 'manufacturer':
          groupKey = ship.manufacturer.name;
          break;
        case 'type':
          const typeKey = ship.type?.toLowerCase() || '';
          groupKey = TYPE_TRANSLATIONS[typeKey] || ship.type || 'Sin tipo';
          break;
        default:
          groupKey = 'Todas';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(ship);
    });

    // Ordenar grupos y naves dentro de cada grupo
    const sortedGroups: Record<string, Ship[]> = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return sortedGroups;
  }, [ships, sortBy]);

  const handleShipClick = (ship: Ship) => {
    if (onShipClick) {
      onShipClick(ship);
    }
  };

  return (
    <div className="ship-catalog-view">
      {Object.entries(groupedShips).map(([groupName, groupShips]) => (
        <div key={groupName} className="catalog-group">
          <h3 className="catalog-group-title">{groupName}</h3>
          <div className="catalog-grid">
            {groupShips.map(ship => {
              const userCount = fleetCounts.user[ship.name] || 0;
              const otherCount = fleetCounts.others[ship.name] || 0;
              return (
                <CatalogItem
                  key={ship.id || ship.name}
                  ship={ship}
                  userCount={userCount}
                  otherCount={otherCount}
                  onClick={() => handleShipClick(ship)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
